import React, { useState } from 'react';
import { ActivityIndicator, FlatList, RefreshControl, StyleSheet, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { GroupedLiteReport } from '../../store/reducers/siteReport/type';
import ReportsListItem from './ReportsListItem';

type ReportsGroupedListProps = {
  groups: GroupedLiteReport[];
  loading: boolean;
  hasMore: boolean;
  profileColor: string;
  onRefresh: () => void;
  onLoadMore: () => void;
};

const GroupSection = ({ group }: { group: GroupedLiteReport }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <View style={styles.groupSection}>
      <TouchableOpacity style={styles.groupHeader} onPress={() => setExpanded(e => !e)} activeOpacity={0.7}>
        <Text style={styles.groupTitle}>{group.title}</Text>
        <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={20} color="#6B7280" />
      </TouchableOpacity>
      {expanded && (
        <View style={styles.groupContent}>
          {group.data.map((item, idx) => (
            <ReportsListItem key={`${group.groupId}-${item.key ?? idx}`} item={item} />
          ))}
        </View>
      )}
    </View>
  );
};

const ReportsGroupedList = ({
  groups,
  loading,
  hasMore,
  profileColor,
  onRefresh,
  onLoadMore
}: ReportsGroupedListProps) => {
  const { t } = useTranslation('home');

  const renderGroup = ({ item }: { item: GroupedLiteReport }) => <GroupSection group={item} />;

  const keyExtractor = (item: GroupedLiteReport) => `group-${item.groupId}`;

  const ListEmptyComponent = loading ? null : (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyTitle}>{t('reports.noReports')}</Text>
      <Text style={styles.emptySubtitle}>{t('reports.noReportsHint')}</Text>
    </View>
  );

  const ListFooterComponent = hasMore ? (
    <View style={styles.footerLoader}>
      <ActivityIndicator color={profileColor} size="small" />
    </View>
  ) : null;

  return (
    <FlatList
      data={groups}
      renderItem={renderGroup}
      keyExtractor={keyExtractor}
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.3}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      refreshControl={
        <RefreshControl refreshing={loading && groups.length === 0} onRefresh={onRefresh} tintColor={profileColor} />
      }
      contentContainerStyle={[styles.listContent, groups.length === 0 && styles.emptyFlex]}
    />
  );
};

export default ReportsGroupedList;

const styles = StyleSheet.create({
  emptyFlex: { flex: 1 },
  listContent: {
    paddingTop: 2,
    paddingBottom: 100
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60
  },
  emptyTitle: {
    fontSize: 18,
    color: '#25282B',
    ...fontMaker({ weight: 'Bold' }),
    marginBottom: 8
  },
  emptySubtitle: {
    fontSize: 13,
    color: '#7C7C7C',
    textAlign: 'center',
    width: 240
  },
  groupSection: {
    marginBottom: 2
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E7EDF3'
  },
  groupContent: {
    paddingTop: 6
  },
  groupTitle: {
    fontSize: 14,
    color: '#374151',
    ...fontMaker({ weight: 'SemiBold' })
  },
  footerLoader: {
    paddingVertical: 16,
    alignItems: 'center'
  }
});
